<template>
	<teleport :to="teleportId">
		<transition :css="false" appear @appear="onAppear" @enter="onEnter" @leave="onLeave">
			<div v-if="isDialogOpen" class="app-dialog" v-bind="$attrs">
				<div ref="backdrop" class="app-dialog__backdrop" @click="onBackdropClick" />

				<div ref="content" class="app-dialog__content">
					<slot v-bind="{ close }" />
				</div>
			</div>
		</transition>
	</teleport>
</template>

<script lang="ts">
import gsap from 'gsap';
import { defineComponent } from 'vue';

import { Popup, usePopup } from '@/common/popup';
import { useElementRefs } from '@/composables';

import { DialogSystem } from '../DialogSystem';

export const PREVENT_SCROLLING_CLASS = 'prevent-scrolling';

export default defineComponent({
	inheritAttrs: false,

	props: {
		isOpen: {
			type: Boolean,
		},

		requireAction: {
			type: Boolean,
		},
	},

	emits: ['update:isOpen', 'close'],

	setup(props, { emit }) {
		const dialog = new Popup(props.isOpen);
		const { isOpen: isDialogOpen, teleportId } = usePopup(dialog, DialogSystem, props, emit);

		return {
			...useElementRefs(['backdrop', 'content'] as const, true),

			isDialogOpen,
			teleportId,

			instance: dialog,
			open: () => dialog.open(),
			close: () => dialog.close(),
		};
	},

	methods: {
		onBackdropClick() {
			if (!this.requireAction) {
				this.close();
			} else {
				this.bloop();
			}
		},

		onAppear(el: Element, done: () => void) {
			const { content, backdrop } = this.getElements(el);

			document.body.classList.add(PREVENT_SCROLLING_CLASS);

			const tl = gsap.timeline({
				onComplete: done,
			});

			tl.fromTo(
				backdrop,
				{
					autoAlpha: 0,
				},
				{
					duration: 0.6,
					autoAlpha: 1,
					ease: 'power4.out',
				},
				0,
			);
			tl.fromTo(
				content,
				{
					alpha: 0,
					scale: 0.85,
				},
				{
					duration: 0.3,
					scale: 1,
					alpha: 1,
					ease: 'power4.out',
				},
				0,
			);
		},

		onEnter(el: Element, done: () => void) {
			this.onAppear(el, done);
		},

		onLeave(el: Element, done: () => void) {
			document.body.classList.remove(PREVENT_SCROLLING_CLASS);

			const { content, backdrop } = this.getElements(el);
			const tl = gsap.timeline({
				onComplete: () => {
					done();
					this.$emit('close');
				},
			});

			tl.to(
				content,
				{
					duration: 0.6,
					autoAlpha: 0,
					pointerEvents: 'none',
					ease: 'power4.out',
				},
				0,
			);

			tl.to(
				backdrop,
				{
					alpha: 0,
					duration: 0.4,
					ease: 'power4.out',
				},
				0,
			);
		},

		bloop() {
			const { content } = this;

			if (!content) return;

			gsap.killTweensOf(content);

			const tl = gsap.timeline();

			tl.to(content, {
				scale: 1.05,
				ease: 'power4.out',
				duration: 0.1,
			});

			tl.to(content, {
				scale: 1,
				ease: 'back.out',
				duration: 0.2,
			});
		},

		getElements(el: Element) {
			return {
				content: el.querySelector('.app-dialog__content'),
				backdrop: el.querySelector('.app-dialog__backdrop'),
			};
		},
	},
});
</script>

<style lang="scss">
.app-dialog {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 8rem;
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 10000;
	color: $color-light;

	&__backdrop {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		background: rgba($color-gray, 0.4);
	}

	&__content {
		@include dropshadow-selected();
		display: flex;
		width: 43rem;
		padding: 2.4rem 3.2rem 3.2rem;
		position: relative;
		z-index: 10;
		border-radius: 1.6rem;
		background: $color-white;
	}
}
</style>
